// Copyright 2025, University of Maryland, All Rights Reserved
import { apiClient } from './api-client'
import type {
    WorkspaceBill,
    WorkspaceBillsResponse,
    ListWorkspaceBillsParams,
    ProcessWorkspaceBillActionRequest
} from '@/types/workspace-bill'
import type { CreateJobResponse } from '@/types/job'
import { useMutation } from '@tanstack/react-query'
import { getActiveWorkspace } from '@umd-dit-ai-workspace/workspaces-component'

// The WorkspaceBill type already exists in @/types/workspace-bill.ts and uses snake_case
// No need for transformation - API returns snake_case directly

// AI Review response type definition - generic for any schema-generated data
export interface AiReviewData {
  bill_number: string;
  session_code: string;
  version: string;
  action: string;
  title?: string;
  [sectionKey: string]: unknown; // Any schema-generated sections at top level
}

// Helper to get workspace path prefix
const getWorkspacePath = (): string => {
  const workspace = getActiveWorkspace();
  if (!workspace?.orgId || !workspace?.wsId) {
    throw new Error('No workspace selected');
  }
  return `/org/${workspace.orgId}/ws/${workspace.wsId}`;
};

export const workspaceBillsApi = {
    getWorkspaceBill: async (session_code: string, bill_number: string): Promise<WorkspaceBill> => {
        try {
            const encoded_session = encodeURIComponent(session_code)
            const response = await apiClient.get<WorkspaceBill>(
              `${getWorkspacePath()}/session/${encoded_session}/bills/${bill_number}`
            )
            return response.data
        } catch (error) {
            console.error('Failed to fetch workspace bill:', error)
            throw error
        }
    },
    listWorkspaceBills: async ({min_impact_score, session}: ListWorkspaceBillsParams): Promise<WorkspaceBillsResponse> => {
        const params = new URLSearchParams()
        
        // Add impact score filtering
        if (min_impact_score !== undefined) {
            params.append('min_impact_score', min_impact_score.toString())
            console.log(`[workspaceBillsApi] Filtering by min_impact_score=${min_impact_score}`)
        } else {
            console.error(`[workspaceBillsApi] No min_impact_score provided`)
            throw Error("No Impact Score Provided")
        }
        
        // Add session parameter
        if (session) {
            params.append('session', session)
            console.log(`[workspaceBillsApi] Using session=${session}`)
        }

        try {
            const request_url = `${getWorkspacePath()}/bills?${params.toString()}`
            console.log(`[workspaceBillsApi] Making request to: ${request_url}`)

            // Make the API request
            const response = await apiClient.get<WorkspaceBillsResponse>(request_url)
            console.log(`[workspaceBillsApi] Response status: ${response.status}`)
            console.log(`[workspaceBillsApi] Response: ${response.data.items.length} items, next_token: ${response.data.next_token ? 'present' : 'null'}`);

            // Validate response structure
            if (!response.data || !Array.isArray(response.data.items)) {
                console.error('[workspaceBillsApi] Invalid response structure:', response.data);
                throw new Error('Invalid response structure from API');
            }

            return response.data
        } catch (error) {
            console.error('Failed to fetch workspace bills:', error instanceof Error ? error.message : String(error))
            throw error
        }
    },
    processWorkspaceBillAction: async ({bill_number, action, session}: ProcessWorkspaceBillActionRequest): Promise<CreateJobResponse> => {
        // This maps to /org/{org_id}/ws/{ws_id}/jobs/process-bills/{bill_number}
        const params = new URLSearchParams()
        params.append('bill_action', action)
        if (session) {
            params.append('session', session)
        }

        const response = await apiClient.post<CreateJobResponse>(
            `${getWorkspacePath()}/jobs/process-bills/${bill_number}?${params.toString()}`,
            undefined
        )
        return response.data
    },
    getAIReview: async (session: string, bill_number: string): Promise<AiReviewData> => {
        try {
            const encoded_session = encodeURIComponent(session)
            const response = await apiClient.get<AiReviewData>(
              `${getWorkspacePath()}/session/${encoded_session}/bills/${bill_number}/ai-review`
            )
            
            let data = response.data as Record<string, unknown> & Partial<AiReviewData>;
            
            // Handle case where data is wrapped in an analysis object with raw_output
            const analysis_obj = data.analysis as {raw_output?: string, [key: string]: unknown} | undefined;
            if (analysis_obj && analysis_obj.raw_output) {
                try {
                    // Extract JSON from raw_output (remove ```json and ``` wrapper if present)
                    const raw_output = analysis_obj.raw_output;
                    const json_match = raw_output.match(/```json\s*(.*?)\s*```/s);
                    const json_string = json_match ? json_match[1] : raw_output;
                    
                    // Parse the extracted JSON and use it directly
                    const parsed_data = JSON.parse(json_string);
                    data = {
                        bill_number: data.bill_number,
                        session_code: data.session_code,
                        version: data.version,
                        action: data.action,
                        ...parsed_data
                    };
                } catch (parse_error) {
                    console.warn('Failed to parse raw_output JSON:', parse_error);
                    // Fall back to original data if parsing fails
                }
            }
            // Handle case where report data is nested under analysis (most common case)
            else if (analysis_obj && Object.keys(analysis_obj).some(key => key !== 'raw_output' && typeof analysis_obj[key] === 'object')) {
                // Extract report data from analysis and flatten to top level
                data = {
                    bill_number: data.bill_number,
                    session_code: data.session_code,
                    version: data.version,
                    action: data.action,
                    ...(analysis_obj as Record<string, unknown>)
                };
            }
            
            return data as AiReviewData;
        } catch (error) {
            console.error('Failed to fetch AI review:', error)
            throw error
        }
    }
}
export const useGetAIReview = () => {
    return useMutation({
        mutationFn: ({ session, bill_number }: { session: string, bill_number: string }) =>
            workspaceBillsApi.getAIReview(session, bill_number)
    })
}
